import { JSONSchema } from 'models';

const isPlainObject = (value: unknown): value is Record<string, any> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const mergeDefaultsInto = (
  schemaProps: JSONSchema,
  current: Record<string, any> | undefined,
  keys: string[],
): { value: Record<string, any> | undefined, changed: boolean } => {
  let result: Record<string, any> | undefined = current;
  let copied = false;

  const ensureCopy = () => {
    if (!copied) {
      result = { ...(current || {}) };
      copied = true;
    }
  };

  keys.forEach(key => {
    const propSchema = schemaProps[key];
    if (!propSchema) return;

    const currentValue = result?.[key];

    if (typeof currentValue === 'undefined' && typeof propSchema.default_value !== 'undefined') {
      ensureCopy();
      (result as Record<string, any>)[key] = propSchema.default_value;
      return;
    }

    const nested = propSchema.properties;
    if (!nested) return;
    if (typeof currentValue !== 'undefined' && !isPlainObject(currentValue)) return;

    const nestedResult = mergeDefaultsInto(nested, currentValue, Object.keys(nested));
    if (nestedResult.changed) {
      ensureCopy();
      (result as Record<string, any>)[key] = nestedResult.value;
    }
  });

  return { value: result, changed: copied };
};

export const mergeSchemaDefaults = <T extends Record<string, any>>(
  schemaProps: JSONSchema | undefined,
  current: T,
  options?: { fields?: string[] },
): T => {
  if (!schemaProps) return current;

  const keys = options?.fields ?? Object.keys(schemaProps);
  const { value, changed } = mergeDefaultsInto(schemaProps, current, keys);
  return (changed ? value : current) as T;
};
